import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from 'react-bootstrap';
import { toast } from 'react-toastify';
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import DeleteAlert from "../../Common/Layout/DeleteAlert";
import moment from "moment";
import "../Package/Package.css";
import "../Package/PackageForm.css";

// 1. IMPORT BOTH LISTS (Crucial for the "Dxb" -> "Dubai" translation)
import hotelSearchData from "../../Data/HotelCities.json"; 
import airportData from "../../Data/AirportList.json"; 

// --- Change Hotel Modal ---
const ChangeHotelModal = ({ show, onHide, hotels, isLoading, onSelect, currentlySelectedHotel }) => {
    const savedHotelName = currentlySelectedHotel?.hotelName;

    return (
        <Modal show={show} onHide={onHide} size="xl" centered dialogClassName="package-hotel-modal">
            <Modal.Header closeButton><Modal.Title><i className="ri-hotel-line me-2"></i>Change Hotel</Modal.Title></Modal.Header>
            <Modal.Body>
                <div className="list-group" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                    {isLoading && (
                        <div className="text-center p-5">
                            <Spinner animation="border" />
                            <p className="mt-2 mb-0">Searching for alternative hotels...</p>
                        </div>
                    )}
                    {!isLoading && hotels && hotels.length > 0 ? (
                        hotels.slice(0, 50).map((hotel) => {
                            const isSelected = savedHotelName === hotel.hotelName;
                            return (
                                <div key={hotel.hotelId} className="list-group-item packagehotelmodel-card-item">
                                    <div className="packagehotelmodel-card-image">
                                        <img src={hotel.mainImage || "/assets/images/Default_Hotel.jpg"} alt={hotel.hotelName} onError={(e) => e.target.src = "/assets/images/Default_Hotel.jpg"}/>
                                    </div>
                                    <div className="packagehotelmodel-card-details">
                                        <h5 className="packagehotelmodel-name mb-1">{hotel.hotelName}</h5>
                                        <div className="packagehotelmodel-rating mb-2">
                                            {Array.from({ length: 5 }).map((_, i) => <i key={i} className={`ri-star-${i < hotel.rating ? 'fill' : 'line'}`}></i>)}
                                        </div>
                                        <p className="packagehotelmodel-address text-muted mb-0"><i className="ri-map-pin-line"></i> {hotel.address || hotel.cityName}</p>
                                    </div>
                                    <div className="packagehotelmodel-card-price-action">
                                        <div className="price">INR {hotel.totalSalePrice?.toFixed(2)}</div>
                                        <p className="price-note text-muted">per stay</p>
                                        <Button variant={isSelected ? "success" : "primary"} size="sm" onClick={() => onSelect(hotel)} className="mt-2 w-100">{isSelected ? "SELECTED" : "SELECT"}</Button>
                                    </div>
                                </div>
                            );
                        })
                    ) : (!isLoading && <div className="text-center p-4"><h5>No hotels found.</h5></div>)}
                </div>
            </Modal.Body>
        </Modal>
    );
};

// --- MAIN COMPONENT ---
const HolidayHotelBooking = ({ holidayId, packageDetails, onUpdate }) => {
    const initialFieldValues = {
        packageHotelBookingId: "",
        hotelName: "", city: "", checkInDate: "", checkOutDate: "", 
        nights: 0, roomType: "", numberOfRooms: 1, price: 0
    };

    const { user } = useAuth();
    const [lists, setLists] = useState([]);
    const [values, setValues] = useState(initialFieldValues);
    const [errors, setErrors] = useState({});
    const [isFormVisible, setIsFormVisible] = useState(false);
    const [btnLoading, setBtnLoading] = useState(false);
    
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const [showChangeHotelModal, setShowChangeHotelModal] = useState(false);
    const [alternativeHotels, setAlternativeHotels] = useState([]);
    const [isModalLoading, setIsModalLoading] = useState(false);
    const [hotelToUpdate, setHotelToUpdate] = useState(null);

    // Load Data
    useEffect(() => {
        if (packageDetails && packageDetails.holidayHotel) {
            try {
                const parsed = typeof packageDetails.holidayHotel === 'string' 
                    ? JSON.parse(packageDetails.holidayHotel) 
                    : packageDetails.holidayHotel;

                const mapped = parsed.map((h, index) => ({
                    packageHotelBookingId: h.packageHotelBookingId || h.PackageHotelBookingId || `temp-${index}`,
                    hotelName: h.hotelName || h.HotelName,
                    city: h.city || h.City,
                    checkInDate: h.checkInDate || h.CheckInDate,
                    checkOutDate: h.checkOutDate || h.CheckOutDate,
                    nights: h.nights || h.Nights,
                    roomType: h.roomType || h.RoomType,
                    numberOfRooms: h.numberOfRooms || h.NumberOfRooms,
                    price: h.price || h.Price
                }));
                setLists(mapped);
            } catch (e) { setLists([]); }
        } else { setLists([]); }
    }, [packageDetails]);

    // Calc Nights
    useEffect(() => {
        if (values.checkInDate && values.checkOutDate) {
            const checkIn = new Date(values.checkInDate);
            const checkOut = new Date(values.checkOutDate);
            if (checkOut > checkIn) {
                const diffTime = Math.abs(checkOut - checkIn);
                const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                setValues(prev => ({ ...prev, nights: diffDays }));
            }
        }
    }, [values.checkInDate, values.checkOutDate]);

    // Save
    const saveHolidayData = async (updatedList) => {
        const formData = new FormData();
        formData.append("holidayId", holidayId);
        formData.append("holidayHotel", JSON.stringify(updatedList));
        if(user?.id) formData.append("userId", user.id);
        try {
            await apiService.update(config.UPDATEHOLIDAY, formData);
            if(onUpdate) onUpdate();
            return true;
        } catch (error) {
            toast.error("Failed to save changes");
            return false;
        }
    };

    // --- KEY FIX: SEARCH LOGIC ---
    const handleChangeHotelClick = async (hotelBooking) => {
        setHotelToUpdate(hotelBooking);
        setShowChangeHotelModal(true);
        setIsModalLoading(true);
        setAlternativeHotels([]);

        try {
            // 1. Get the input (e.g., "Dxb")
            let searchInput = hotelBooking.city ? hotelBooking.city.trim() : "";
            let placeInfo = null;

            // 2. First Try: Direct Match in Hotel Cities (e.g. if input is "Dubai")
            placeInfo = hotelSearchData.find(cityData => 
                cityData.cityName.toLowerCase() === searchInput.toLowerCase()
            );

            // 3. Second Try: If not found, check Airport Codes (e.g. input is "Dxb")
            if (!placeInfo) {
                // Find "Dxb" in Airport List
                const airportInfo = airportData.find(a => 
                    a.airportCode.toLowerCase() === searchInput.toLowerCase()
                );

                if (airportInfo && airportInfo.cityName) {
                    // Extract "Dubai" from "Dubai, UAE"
                    // We split by comma to ignore country part if present
                    const cleanCityName = airportInfo.cityName.split(',')[0].trim();
                    
                    // Now search Hotel Cities again using "Dubai"
                    placeInfo = hotelSearchData.find(cityData => 
                        cityData.cityName.toLowerCase() === cleanCityName.toLowerCase()
                    );
                }
            }

            // 4. Validate Found Data
            if (!placeInfo || !placeInfo.id) {
                // If we still can't find it, we can't search
                toast.error(`Could not match city code for "${searchInput}".`);
                setIsModalLoading(false);
                return; 
            }
            
            // 5. Build Request (Matches your Screenshot)
            const hotelSearchRequest = {
                companyId: user.cid || 1, 
                userId: user.id, 
                
                // --- THIS MATCHES YOUR SCREENSHOT ---
                city: placeInfo.cityName,         // Sends "DUBAI"
                cityCode: placeInfo.id.toString(),// Sends "147862"
                country: placeInfo.countryName,   // Sends "UNITED ARAB EMIRATES"
                // -----------------------------------

                checkInDate: hotelBooking.checkInDate, 
                checkOutDate: hotelBooking.checkOutDate, 
                totalGuests: (packageDetails.adults || 1) + (packageDetails.children || 0),
                hotelRooms: [{
                    adults: packageDetails.adults || 1, 
                    kids: packageDetails.children || 0, 
                    kidsAge: []
                }],
                rating: [3, 4, 5], 
                currency: "INR", 
                nationality: "106",
            };
            
            const res = await apiService.post(config.SEARCHHOTELALTERNATIVES, hotelSearchRequest);
            setAlternativeHotels(res.data?.commonHotelDetails || []);

        } catch (error) {
            console.error("Search failed:", error);
            toast.error("Error searching for hotels.");
        } finally {
            setIsModalLoading(false);
        }
    };

    // Update Selection
    const handleSelectNewHotel = async (selectedHotel) => {
        if (!hotelToUpdate || !selectedHotel) return;
        setBtnLoading(true);
        
        const updatedHotel = {
            ...hotelToUpdate,
            hotelName: selectedHotel.hotelName,
            roomType: selectedHotel.commonHotelDefaultRoom?.roomType || hotelToUpdate.roomType || "Standard",
            price: selectedHotel.totalSalePrice,
        };

        const newList = lists.map(x => x.packageHotelBookingId === hotelToUpdate.packageHotelBookingId ? updatedHotel : x);
        const success = await saveHolidayData(newList);
        if(success) {
            
            setShowChangeHotelModal(false);
        }
        setBtnLoading(false);
    };

    // Standard Handlers
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (validate()) {
            setBtnLoading(true);
            let newList = [...lists];
            const currentId = values.packageHotelBookingId || crypto.randomUUID();
            const newHotel = { ...values, packageHotelBookingId: currentId };
            const index = newList.findIndex(x => x.packageHotelBookingId === currentId);
            if (index !== -1) newList[index] = newHotel;
            else newList.push(newHotel);
            const success = await saveHolidayData(newList);
            if(success) {
                setIsFormVisible(false);
                resetForm();
            }
            setBtnLoading(false);
        }
    };

    const handleDelete = async () => {
        if (itemToDelete) {
            const newList = lists.filter(x => x.packageHotelBookingId !== itemToDelete);
            const success = await saveHolidayData(newList);
            if(success)
            setShowDeleteModal(false);
            setItemToDelete(null);
        }
    };

    const handleAddNewClick = () => { resetForm(); setIsFormVisible(true); };
    const handleCancel = () => { resetForm(); setIsFormVisible(false); };
    const showEditDetails = (data) => {
        const formatted = { ...data, checkInDate: moment(data.checkInDate).format('YYYY-MM-DD'), checkOutDate: moment(data.checkOutDate).format('YYYY-MM-DD') };
        setValues(formatted);
        setIsFormVisible(true);
    };
    const resetForm = () => { setValues(initialFieldValues); setErrors({}); };
    const handleInputChange = (e) => { const { name, value } = e.target; setValues({ ...values, [name]: value }); };
    const validate = () => { 
        let t = {}; 
        t.hotelName = !!values.hotelName; t.city = !!values.city; t.checkInDate = !!values.checkInDate; t.checkOutDate = !!values.checkOutDate; t.numberOfRooms = values.numberOfRooms > 0; 
        setErrors(t); return Object.values(t).every(x=>x); 
    };
    const applyErrorClass = (field) => errors[field] === false ? " form-control-danger" : "";

    return (
        <div className="PackageHotel-wrapper">
            <div className="card PackageHotel-card">
                <div className="card-body">
                    <div className="PackageHotel-header">
                        <h4 className="header-title">Hotel Bookings</h4>
                        {!isFormVisible && (<button type="button" className="btn btn-primary PackageHotel-btn-gradient" onClick={handleAddNewClick}><i className="ri-add-line me-1"></i> Add Hotel Stay</button>)}
                    </div>
                    {isFormVisible && (
                        <div className="PackageHotel-form-container">
                            <h5>{values.packageHotelBookingId ? "Edit Hotel" : "Add Hotel"}</h5>
                            <form onSubmit={handleSubmit}>
                                <div className="row">
                                    <div className="col-lg-3 mb-3"><label>Hotel Name</label><input type="text" name="hotelName" value={values.hotelName} onChange={handleInputChange} className={"form-control"+applyErrorClass('hotelName')} placeholder="Grand Hyatt"/>{errors.hotelName === false && <div className="validationerror">Please enter a valid hotel name</div>}</div>
                                    <div className="col-lg-3 mb-3"><label>City</label><input type="text" name="city" value={values.city} onChange={handleInputChange} className={"form-control"+applyErrorClass('city')} placeholder="e.g. Dubai" />{errors.city === false && <div className="validationerror">Please enter a valid city</div>}</div>
                                    <div className="col-lg-3 mb-3"><label>Check-In</label><input type="date" name="checkInDate" value={values.checkInDate} onChange={handleInputChange} className={"form-control"+applyErrorClass('checkInDate')} />{errors.checkInDate === false && <div className="validationerror">Please enter a valid check-in date</div>}</div>
                                    <div className="col-lg-3 mb-3"><label>Check-Out</label><input type="date" name="checkOutDate" value={values.checkOutDate} onChange={handleInputChange} className={"form-control"+applyErrorClass('checkOutDate')} />{errors.checkOutDate === false && <div className="validationerror">Please enter a valid check-out date</div>}</div>
                                    <div className="col-lg-3 mb-3"><label>Room Type</label><select name="roomType" value={values.roomType} onChange={handleInputChange} className="form-select"><option value="">Select</option><option value="Standard">Standard</option><option value="Deluxe">Deluxe</option><option value="Suite">Suite</option></select></div>
                                    <div className="col-lg-3 mb-3"><label>No. of Rooms</label><input type="number" name="numberOfRooms" value={values.numberOfRooms} onChange={handleInputChange} className={"form-control"+applyErrorClass('numberOfRooms')} min="1"/></div>
                                    <div className="col-lg-3 mb-3"><label>Price</label><input type="number" name="price" value={values.price} onChange={handleInputChange} className="form-control" /></div>
                                </div>
                                <div className="d-flex justify-content-end gap-2">
                                    <button type="button" className="btn btn-light" onClick={handleCancel}>Cancel</button>
                                    <button type="submit" className="btn btn-primary" disabled={btnLoading}>{btnLoading ? "Saving..." : "Submit"}</button>
                                </div>
                            </form>
                        </div>
                    )}
                    <div className="table-responsive mt-4">
                        <table className="table PackageHotel-table">
                            <thead><tr><th>Hotel</th><th>Dates</th><th className="text-center">Nights</th><th>Details</th><th>Price</th><th className="text-center">Actions</th></tr></thead>
                            <tbody>
                                {lists.length > 0 ? lists.map((v, i) => (
                                    <tr key={v.packageHotelBookingId || i}>
                                        <td><div className="fw-medium">{v.hotelName}</div><div className="location-text"><i className="ri-map-pin-2-fill me-1"></i>{v.city}</div></td>
                                        <td>{moment(v.checkInDate).format("ddd, DD MMM")} - {moment(v.checkOutDate).format("ddd, DD MMM")}</td>
                                        <td className="text-center"><span className="PackageHotel-nights-badge">{v.nights}</span></td>
                                        <td><div>{v.roomType}</div><div className="rooms-text">Rooms: {v.numberOfRooms}</div></td>
                                        <td>{v.price}</td>
                                        <td className="text-center">
                                            <div className="d-flex gap-2 justify-content-center">
                                                <button className="PackageHotel-action-btn change" title="Change Hotel" onClick={() => handleChangeHotelClick(v)} style={{ backgroundColor: "#ffc107", color: "#fff", borderRadius: "50%", width: "32px", height: "32px", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer" }}><i className="ri-swap-line" /></button>
                                                <button className="PackageHotel-action-btn edit" onClick={() => showEditDetails(v)}><i className="ri-pencil-fill" /></button>
                                                <button className="PackageHotel-action-btn delete" onClick={() => { setItemToDelete(v.packageHotelBookingId); setShowDeleteModal(true); }}><i className="ri-delete-bin-fill" /></button>
                                            </div>
                                        </td>
                                    </tr>
                                )) : (<tr><td colSpan="6"><div className="PackageHotel-empty-state"><i className="ri-hotel-line empty-icon"></i><h5>No Hotels Added Yet</h5></div></td></tr>)}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
            <DeleteAlert show={showDeleteModal} handleClose={() => setShowDeleteModal(false)} handleDelete={handleDelete} />
            <ChangeHotelModal show={showChangeHotelModal} onHide={() => setShowChangeHotelModal(false)} hotels={alternativeHotels} isLoading={isModalLoading} onSelect={handleSelectNewHotel} currentlySelectedHotel={hotelToUpdate} />
        </div>
    );
};

export default HolidayHotelBooking;